/**
 * Where the discovery for a case physically is. Mirrors backend/src/config/discoveryLocations.ts.
 *
 * Lloyd, Aug 19 2026: "a tab button for discovery requested and discovery received and location
 * (emailed to Lloyd, mailed thumb drive, waiting to be picked up from court; in DropBox or other
 * places)".
 *
 * The location is a code rather than free text because the question the firm actually asks is "can
 * I look at it now, or does somebody have to drive to the court?" — a small closed set whose
 * members mean different things operationally. OTHER keeps a note beside it for the genuinely open
 * case.
 */

export const DISCOVERY_LOCATIONS = [
  'EMAILED_LLOYD',
  'THUMB_DRIVE_MAILED',
  'AWAITING_COURT_PICKUP',
  'DROPBOX',
  'OTHER',
] as const;

export type DiscoveryLocation = (typeof DISCOVERY_LOCATIONS)[number];

export const DISCOVERY_LOCATION_LABELS: Record<DiscoveryLocation, string> = {
  EMAILED_LLOYD: 'Emailed to Lloyd',
  THUMB_DRIVE_MAILED: 'Thumb drive posted to the office',
  AWAITING_COURT_PICKUP: 'Waiting to be collected from the court',
  DROPBOX: 'In Dropbox',
  OTHER: 'Somewhere else — see the note',
};

/** The places that mean the firm already has it. Anything else is an errand somebody owes. */
export const DISCOVERY_LOCATIONS_IN_HAND: ReadonlySet<string> = new Set([
  'EMAILED_LLOYD',
  'THUMB_DRIVE_MAILED',
  'DROPBOX',
]);

export function isDiscoveryLocation(value: unknown): value is DiscoveryLocation {
  return (
    typeof value === 'string' &&
    (DISCOVERY_LOCATIONS as readonly string[]).includes(value)
  );
}

/** A stored code that no longer exists reads as "not recorded" rather than rendering raw. */
export function describeDiscoveryLocation(location: string | null | undefined): string {
  return isDiscoveryLocation(location)
    ? DISCOVERY_LOCATION_LABELS[location]
    : 'Location not recorded';
}

export interface DiscoveryRecord {
  requestedAt: string | null;
  receivedAt: string | null;
  location: string | null;
  note: string | null;
}

/**
 * Things worth a person's attention. None of them block a save — staff record what they know when
 * they know it, and a half-filled record beats a refused one. Mirrors describeDiscoveryGaps on the
 * server; kept here so the panel can warn without a round trip.
 */
export function describeDiscoveryGaps(record: DiscoveryRecord): string[] {
  const gaps: string[] = [];
  const location = isDiscoveryLocation(record.location) ? record.location : null;

  if (record.receivedAt && !location) {
    gaps.push('Marked received but no location is recorded, so nobody knows where it is.');
  }
  if (record.receivedAt && !record.requestedAt) {
    gaps.push('Marked received with no request date. Fill the request date in if you know it.');
  }
  if (record.requestedAt && record.receivedAt && record.receivedAt < record.requestedAt) {
    gaps.push('The received date is before the request date. One of the two is likely mistyped.');
  }
  if (location === 'AWAITING_COURT_PICKUP') {
    gaps.push('Still at the court — somebody has to collect it.');
  }
  if (location === 'OTHER' && !record.note?.trim()) {
    gaps.push('Location is “somewhere else” with no note, which records nothing.');
  }
  return gaps;
}
